import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { useToast } from '../contexts/ToastContext';
import { posthog } from '../main.jsx';
import { safeFetch } from '../utils/safeFetch';
import { apiUrl } from '../config/api';
import { resolvePostLoginRedirect } from '../lib/navigation/resolvePostLoginRedirect'; // #1521/#1562
import { toUserFacingMessage } from '../utils/userFacingError.js';
import { safeSessionStorage } from '../utils/safeStorage.js';

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const googleSectionRef = useRef(null);

  // Store intended destination for post-login redirect (Issue #335: shareable links)
  // #3637: guarded. A storage-blocked browser throws SecurityError here, and
  // this effect runs on every redirect to /login (ProtectedRoute's state.from),
  // so an unguarded write crashed the page the storage stop is shown on.
  useEffect(() => {
    const from = location.state?.from?.pathname;
    if (from && from !== '/login' && from !== '/') {
      safeSessionStorage.setItem('loginRedirect', from);
    }
  }, [location.state]);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [googleEmphasised, setGoogleEmphasised] = useState(false);

  const { requestMagicLink, loginWithGoogle, sessionExpiredMessage, clearSessionExpiredMessage } = useAuth();
  const branding = useBranding();
  const [orgBranding, setOrgBranding] = useState(null);

  // Pre-fill email from URL ?email= param so Google One Tap and the form show the right account (Issue #1155)
  const params = new URLSearchParams(location.search);
  const urlEmail = params.get('email');
  const urlSigninHint = params.get('signin'); // 'google' deep-links from invitation/reminder emails (#1155)

  useEffect(() => {
    if (urlEmail && !email) {
      setEmail(decodeURIComponent(urlEmail));
    }
    // We only want to seed once on mount, hence the guarded check above
  }, [urlEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch org branding from ?org= query param for pre-auth branding (Issue #514)
  useEffect(() => {
    const orgSlug = params.get('org');
    if (!orgSlug) return;

    // Use the canonical API base resolver (config/api.js, VITE_API_BASE_URL).
    // 404 = no custom branding for that org slug; fall back to defaults.
    safeFetch(apiUrl(`/auth/org-branding/${encodeURIComponent(orgSlug)}`), {
      allowErrorStatuses: [404],
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.branding) setOrgBranding(data.branding);
      })
      .catch(() => {}); // Fall back to default branding on error
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use org-specific branding if available (pre-auth), otherwise use context branding
  const displayBranding = orgBranding ? {
    appName: orgBranding.app_name || branding.appName,
    primaryColor: orgBranding.primary_color || branding.primaryColor,
    poweredBy: orgBranding.powered_by || branding.poweredBy,
  } : branding;

  // Clear session expired message when component unmounts or after showing it
  useEffect(() => {
    // Clear after a delay to ensure user sees it
    if (sessionExpiredMessage) {
      const timer = setTimeout(() => {
        clearSessionExpiredMessage();
      }, 10000); // Clear after 10 seconds
      return () => clearTimeout(timer);
    }
  }, [sessionExpiredMessage, clearSessionExpiredMessage]);

  // Issue #1155: ?signin=google deep-link emphasises the Google button + activates One Tap.
  // Codex P1: this is a UI-state hint only; the gesture stays click-driven on the rendered button.
  // (useGoogleLogin auto-trigger would return access tokens and 400 at /auth/google.)
  useEffect(() => {
    if (urlSigninHint !== 'google') return;
    if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) return;

    setGoogleEmphasised(true);
    posthog?.capture?.('auth_deep_link_triggered', {
      provider: 'google',
      has_email_hint: !!urlEmail,
    });

    // Scroll the Google button into view + flash an emphasis ring on mount
    const t = setTimeout(() => {
      googleSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    // Strip the ?signin param from URL so refresh doesn't re-trigger (Modal/Async invariant)
    const cleanParams = new URLSearchParams(location.search);
    cleanParams.delete('signin');
    const newSearch = cleanParams.toString();
    navigate(
      { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' },
      { replace: true }
    );

    return () => clearTimeout(t);
    // We intentionally only run this once on mount when the hint is present
  }, [urlSigninHint]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setError('');
    posthog?.capture?.('auth_provider_selected', { provider: 'magic_link', surface: 'login_page' });

    try {
      const response = await requestMagicLink(email);
      setMessage(response.message);
      setEmail(''); // Clear the form
    } catch (err) {
      setError(toUserFacingMessage(err, t, 'Failed to send magic link'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setError('');
    setMessage('');
    posthog?.capture?.('auth_provider_selected', { provider: 'google', surface: 'login_page' });
    try {
      const response = await loginWithGoogle(credentialResponse.credential);
      if (response.success) {
        // Q-B: soft toast if Google account email differs from the URL invitation email (#1155)
        const expectedEmail = urlEmail ? decodeURIComponent(urlEmail).toLowerCase() : null;
        const actualEmail = response.user?.email?.toLowerCase();
        if (expectedEmail && actualEmail && expectedEmail !== actualEmail) {
          showToast(
            t('auth.googleMismatchToast', {
              googleEmail: response.user.email,
              inviteEmail: decodeURIComponent(urlEmail),
            }),
            'info',
            10000,
          );
        }

        // Redirect to stored destination or the flag-resolved learner home
        // (Issue #335; #1521: from the login RESPONSE branding — context is
        // stale inside this closure; #1562: a stored home alias is residue,
        // only deep links win).
        const redirectTo = resolvePostLoginRedirect(
          // eslint-disable-next-line no-restricted-globals -- #3637 follow-up: post-login only (login() already stored a token, so storage is readable here)
          sessionStorage.getItem('loginRedirect'),
          response.user?.branding,
        );
        // eslint-disable-next-line no-restricted-globals -- #3637 follow-up: post-login only, same as above
        sessionStorage.removeItem('loginRedirect');
        navigate(redirectTo);
      }
    } catch (err) {
      setError(toUserFacingMessage(err, t, 'Failed to sign in with Google'));
    } finally {
      setIsLoading(false);
    }
  };

  const googleEnabled = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

  return (
    <div className="login-page" style={{ flexDirection: 'column' }}>
      <div style={{ maxWidth: '500px', width: '100%' }}>
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          style={{ display: 'block', marginBottom: '0.75rem', color: '#666', textDecoration: 'none', fontSize: '0.9rem' }}
        >
          {t('login.home')}
        </a>
      </div>
      <div className="login-container">
        <div className="login-header">
          <h1>{displayBranding.appName}</h1>
          {displayBranding.poweredBy && (
            <p style={{ fontSize: '0.85rem', color: '#888', margin: '-0.25rem 0 0.5rem' }}>
              Powered by {displayBranding.poweredBy}
            </p>
          )}
          <h2>{t('login.signInPage')}</h2>
          <p>{t('login.signInSubtitle')}</p>
        </div>

        {sessionExpiredMessage && (
          <div className="info-message session-expired">
            <div className="message-icon">🔒</div>
            <div className="message-content">
              <h3>{t('login.sessionExpiredTitle')}</h3>
              <p>{sessionExpiredMessage}</p>
            </div>
          </div>
        )}

        {/* Google Sign-In (Issue #299, #1155 promoted to primary) */}
        {googleEnabled && (
          <div
            className={`google-login-section${googleEmphasised ? ' emphasised' : ''}`}
            ref={googleSectionRef}
          >
            <div className="auth-section-header">
              <strong>{t('login.quickestSignIn')}</strong>
            </div>
            <div className="google-login-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => {
                  setError(t('login.googleSignInFailed'));
                }}
                theme="outline"
                size="large"
                text="signin_with"
                width="100%"
                useOneTap={googleEmphasised}
                login_hint={urlEmail ? decodeURIComponent(urlEmail) : undefined}
              />
            </div>
          </div>
        )}

        {/* Email magic-link (#1155: now secondary fallback) */}
        <div className={googleEnabled ? 'auth-divider' : ''}>
          {googleEnabled && <span>{t('login.orSignInWithEmail')}</span>}
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">{t('login.emailLabel')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            className="magic-link-button"
            disabled={isLoading || !email.trim()}
          >
            {isLoading ? t('login.sending') : t('login.sendMagicLink')}
          </button>
          <p className="auth-section-helper email-helper">{t('login.emailHelperText')}</p>
        </form>

        {message && (
          <div className="success-message">
            <div className="message-icon">✨</div>
            <div className="message-content">
              <h3>{t('login.magicLinkSent')}</h3>
              <p>{message}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <div className="message-icon">❌</div>
            <div className="message-content">
              <h3>{t('login.oops')}</h3>
              <p>{error}</p>
            </div>
          </div>
        )}

        <div className="login-footer">
          <p>
            <strong>{t('login.secureAuthTitle')}</strong> {t('login.secureAuthDesc')}
          </p>
          <div className="login-footer-links">
            <Link to="/privacy">{t('footer.privacy')}</Link>
            <Link to="/terms">{t('footer.terms')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
